import type { ArticleIndexEntry } from "knowledge-base/_api/knowledge-base-api";
import type { CategoryNode, IndexTree } from "knowledge-base/_views/knowledge-base-index-tree";

/**
 * The drop target that means "take this out of its category".
 *
 * A string rather than a category id because there is no category to name: an article can sit loose
 * in a library, and a curator needs a way to put one back there without inventing a holding pen.
 */
export const UNFILED_DROP_ID = "knowledge-base-unfiled";

/**
 * The order one category's articles should end up in.
 *
 * The whole list, not one article and a position. Positions are integers on the server, so "put
 * this between those two" has no answer when the two are already adjacent — and the browser already
 * knows the order it is showing, so sending it is both simpler and atomic.
 */
export type OrderArticlesIntent = Readonly<{
  /** Absent means the articles end up outside every category. */
  categoryId?: string;
  articleIds: readonly string[];
}>;

/**
 * Works out what a drop should do, or that it should do nothing.
 *
 * Separated from the tree component so the rules can be stated as facts rather than exercised
 * through a simulated drag. The interesting cases are all refusals: a drop outside any target, a
 * drop back where the article already was, and a drop on something that is not a target. Each has
 * to produce no request at all — a no-op that still called the server would rewrite positions
 * nobody asked to change.
 */
export function resolveDropIntent(
  activeArticleId: string,
  overId: string | null,
  tree: IndexTree
): OrderArticlesIntent | null {
  if (!overId || overId === activeArticleId) {
    return null;
  }

  const article = findArticle(tree, activeArticleId);
  if (!article) {
    return null;
  }

  const from = article.categoryId ?? undefined;

  // Dropped on another article: land beside it, in whatever category it lives in.
  const overArticle = findArticle(tree, overId);
  if (overArticle) {
    const to = overArticle.categoryId ?? undefined;
    const siblings = articlesIn(tree, to);
    const withoutMoved = siblings.filter((sibling) => sibling.id !== activeArticleId);
    const index = insertionIndexFor(activeArticleId, overId, siblings);

    if (index === null) {
      return null;
    }

    const reordered = [
      ...withoutMoved.slice(0, index),
      article,
      ...withoutMoved.slice(index),
    ];

    if (from === to && isSameOrder(siblings, reordered)) {
      return null;
    }

    return { categoryId: to, articleIds: reordered.map((entry) => entry.id) };
  }

  // Dropped on a category heading, or on the unfiled zone: append.
  const to = overId === UNFILED_DROP_ID ? undefined : findCategory(tree.categories, overId)?.id;
  if (overId !== UNFILED_DROP_ID && to === undefined) {
    return null;
  }

  if (from === to) {
    return null;
  }

  const destination = articlesIn(tree, to).filter((entry) => entry.id !== activeArticleId);

  return {
    categoryId: to,
    // Appended rather than inserted. Dropping on a heading answers "which category", and guessing
    // a place inside a list nobody was looking at is worse than the end, where the person who
    // filed it will look for it.
    articleIds: [...destination.map((entry) => entry.id), activeArticleId],
  };
}

/**
 * Where a dragged article should be inserted among a list, once it has been taken out of it.
 *
 * Directional within a list: dragging downwards lands the article after the one it was dropped on,
 * dragging upwards lands it before. Always inserting before would read consistently but leave no
 * way to make an article last by dropping it on the last row, which is the obvious thing to try.
 *
 * Coming from a different category there is no direction to read, so it lands in the target's slot
 * and pushes it down.
 *
 * Returns null when the drop makes no sense — the target is not in the list, or is the article
 * being dragged.
 */
export function insertionIndexFor(
  activeArticleId: string,
  overArticleId: string,
  siblings: readonly ArticleIndexEntry[]
): number | null {
  if (activeArticleId === overArticleId) {
    return null;
  }

  const withoutMoved = siblings.filter((sibling) => sibling.id !== activeArticleId);
  const target = withoutMoved.findIndex((sibling) => sibling.id === overArticleId);
  if (target < 0) {
    return null;
  }

  const from = siblings.findIndex((sibling) => sibling.id === activeArticleId);
  const to = siblings.findIndex((sibling) => sibling.id === overArticleId);
  const draggingDownWithinTheList = from >= 0 && to >= 0 && from < to;

  return draggingDownWithinTheList ? target + 1 : target;
}

/**
 * Which edge of a row the insertion line belongs on, so the indicator and the drop agree.
 */
export function dropEdgeFor(
  activeArticleId: string,
  overArticleId: string,
  siblings: readonly ArticleIndexEntry[]
): "top" | "bottom" | null {
  const index = insertionIndexFor(activeArticleId, overArticleId, siblings);
  if (index === null) {
    return null;
  }

  const withoutMoved = siblings.filter((sibling) => sibling.id !== activeArticleId);
  return index > withoutMoved.findIndex((sibling) => sibling.id === overArticleId)
    ? "bottom"
    : "top";
}

/**
 * Prefix marking a drag as a category rather than an article.
 *
 * Categories are drop targets for articles already, so their droppable id stays the plain category
 * id. Giving the *draggable* a prefix is what lets one drop handler tell "file this article here"
 * from "nest this category here" when the target is the same row.
 */
export const CATEGORY_DRAG_PREFIX = "category:";

/**
 * Prefix for the thin strip above a category row, which places a dragged category as its sibling.
 *
 * A separate target from the row itself because dropping *on* a category means nesting inside it,
 * and there would otherwise be no way to express "beside, not inside".
 */
export const CATEGORY_BEFORE_PREFIX = "before-category:";

/** The strip that makes a dragged category top-level. */
export const TOP_LEVEL_DROP_ID = "knowledge-base-top-level";

export const categoryDragId = (categoryId: string) => `${CATEGORY_DRAG_PREFIX}${categoryId}`;
export const categoryBeforeId = (categoryId: string) => `${CATEGORY_BEFORE_PREFIX}${categoryId}`;

export function isCategoryDrag(activeId: string): boolean {
  return activeId.startsWith(CATEGORY_DRAG_PREFIX);
}

/**
 * The order the categories under one parent should end up in.
 */
export type OrderCategoriesIntent = Readonly<{
  /** Absent means they end up at the top level. */
  parentCategoryId?: string;
  categoryIds: readonly string[];
}>;

/**
 * Works out what dropping a category should do, or that it should do nothing.
 *
 * Two shapes of drop. Onto the strip above a category: become its sibling, immediately before it.
 * Onto a category row: become its child, appended. Onto the top-level strip: leave whatever parent
 * it had.
 *
 * Refusals matter more than the successes. Nesting a category inside itself or inside its own
 * descendant would detach that whole branch from the tree — the server refuses it, but a UI that
 * lets you try and then shows an error is worse than one that never offers the drop.
 */
export function resolveCategoryDropIntent(
  activeDragId: string,
  overId: string | null,
  tree: IndexTree
): OrderCategoriesIntent | null {
  if (!overId || !isCategoryDrag(activeDragId)) {
    return null;
  }

  const activeId = activeDragId.slice(CATEGORY_DRAG_PREFIX.length);
  const active = findCategory(tree.categories, activeId);
  if (!active) {
    return null;
  }

  const from = active.parentCategoryId ?? undefined;

  if (overId === TOP_LEVEL_DROP_ID) {
    if (from === undefined) {
      return null;
    }

    return {
      parentCategoryId: undefined,
      categoryIds: [
        ...tree.categories.filter((category) => category.id !== activeId).map((c) => c.id),
        activeId,
      ],
    };
  }

  if (overId.startsWith(CATEGORY_BEFORE_PREFIX)) {
    const targetId = overId.slice(CATEGORY_BEFORE_PREFIX.length);
    if (targetId === activeId) {
      return null;
    }

    const target = findCategory(tree.categories, targetId);
    if (!target) {
      return null;
    }

    const to = target.parentCategoryId ?? undefined;
    if (wouldNest(active, to)) {
      return null;
    }

    const siblings = categoriesUnder(tree, to);
    const withoutMoved = siblings.filter((category) => category.id !== activeId);
    const index = withoutMoved.findIndex((category) => category.id === targetId);
    if (index < 0) {
      return null;
    }

    const reordered = [
      ...withoutMoved.slice(0, index).map((c) => c.id),
      activeId,
      ...withoutMoved.slice(index).map((c) => c.id),
    ];

    if (from === to && isSameIds(siblings.map((c) => c.id), reordered)) {
      return null;
    }

    return { parentCategoryId: to, categoryIds: reordered };
  }

  // Dropped on a category row: nest inside it.
  const parent = findCategory(tree.categories, overId);
  if (!parent || parent.id === activeId || from === parent.id) {
    return null;
  }

  // The drop that would strand a branch: nesting a category inside something it already contains.
  if (isDescendantOf(active, parent.id)) {
    return null;
  }

  return {
    parentCategoryId: parent.id,
    categoryIds: [
      ...parent.children.filter((child) => child.id !== activeId).map((c) => c.id),
      activeId,
    ],
  };
}

/** Whether moving `active` under `parentId` would put it inside its own subtree. */
function wouldNest(active: CategoryNode, parentId: string | undefined): boolean {
  if (parentId === undefined) return false;
  return parentId === active.id || isDescendantOf(active, parentId);
}

/** Whether `categoryId` is somewhere beneath `ancestor`. */
function isDescendantOf(ancestor: CategoryNode, categoryId: string): boolean {
  return ancestor.children.some(
    (child) => child.id === categoryId || isDescendantOf(child, categoryId)
  );
}

function categoriesUnder(tree: IndexTree, parentId: string | undefined): CategoryNode[] {
  if (parentId === undefined) {
    return [...tree.categories];
  }

  return [...(findCategory(tree.categories, parentId)?.children ?? [])];
}

function isSameIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

function isSameOrder(
  left: readonly ArticleIndexEntry[],
  right: readonly ArticleIndexEntry[]
): boolean {
  return (
    left.length === right.length && left.every((entry, index) => entry.id === right[index].id)
  );
}

/**
 * The articles in one category, in the order they are displayed. Undefined means the unfiled list.
 */
function articlesIn(tree: IndexTree, categoryId: string | undefined): ArticleIndexEntry[] {
  if (categoryId === undefined) {
    return [...tree.unfiled];
  }

  return [...(findCategory(tree.categories, categoryId)?.articles ?? [])];
}

function findArticle(tree: IndexTree, articleId: string): ArticleIndexEntry | undefined {
  const inUnfiled = tree.unfiled.find((article) => article.id === articleId);
  if (inUnfiled) {
    return inUnfiled;
  }

  const walk = (categories: readonly CategoryNode[]): ArticleIndexEntry | undefined => {
    for (const category of categories) {
      const hit = category.articles.find((article) => article.id === articleId);
      if (hit) return hit;

      const nested = walk(category.children);
      if (nested) return nested;
    }

    return undefined;
  };

  return walk(tree.categories);
}

function findCategory(
  categories: readonly CategoryNode[],
  categoryId: string
): CategoryNode | undefined {
  for (const category of categories) {
    if (category.id === categoryId) return category;

    const nested = findCategory(category.children, categoryId);
    if (nested) return nested;
  }

  return undefined;
}
